import React, { useMemo } from "react";
import styled from "styled-components";
import { useGetTopicClusteringTreeCounts } from "@/utils/react-query-hooks/aiAnnotation";
import { useAggrMetricsAcrossTimeAndDimension } from "@/components/analysisModules/reports/ReportLanding/comps/WidgetComps/WidgetLineChart/query/useAggrMetricsAcrossTimeAndDimension";
import { topicNodeFilter } from "@/components/analysisModules/TopicClusteringTreeViewV2/scopeFilters";
import { UI_BODY_SEMIBOLD, UI_BODY_SEMIBOLD_SM, UI_BODY_SM, UI_CAPTION_DF, colors2, WrapperCardGrid, ButtonGhostCompact, SkeletonLoader } from "oolib";
// Not on the oolib root barrel — same path the Lenses widget headers use.
import { UI_BODY_SEMIBOLD_SM_DF } from "oolib/dist/v2/components/Typo2";
import { CategorySparklines, ObservationSparkline } from "./CategorySparklines";
import { LeadingCategoryTrend } from "./LeadingCategoryTrend";
import { SentimentBar } from "@/_self_managed/SM_Dashboard/LensesV2/comps/SentimentBar";
import { SignalBadge } from "@/_self_managed/SM_Dashboard/LensesV2/comps/SignalBadge";
import { useSignalCategories } from "./SignalCategoriesContext";
import { DatasetCircles } from "./DatasetCircles";
import { TopicCard, ClickableCard, CardTail, CardFooter, ObservationCount } from "./TopicCard";

/**
 * Layout 2 — grid of wide bucket cards (2-up at ~1000-1200px). Each card:
 * the bucket's label + description, and a single stacked bar of the bucket's
 * signal-CATEGORY distribution (per-anno counts aggregated by the BE).
 * Reuses the LensesV2 SentimentBar and the SignalsWidget category colours so
 * the palette means the same thing everywhere.
 *
 * Clicking a card drills into the bucket: its contents become a white header
 * band, and below sits one full-bleed section per signal category (biggest
 * share first, matching the bar) whose finding cards are the bucket's
 * encapsulations that lean that way — signal pill + encapsulation statement
 * + dataset provenance circles. Topic tags deliberately not shown at this
 * level.
 *
 * Observations no finding took ("unplaced" / "parked") get no cards of their
 * own: at every level they roll up into ONE muted finding-shaped card at the
 * end of the strip (or grid), which opens the same side panel a finding does,
 * listing the observations under it.
 */

// Category order, legend labels, drill-down titles and colours all come from
// the signal-category theme (useSignalCategories — the tenant's own list),
// which CategorySparklines reads too, so the drilldown bar and the
// sparklines under it can never disagree on order.

// The drilldown's header: a white band running edge to edge across the top
// of the content area — no border, no radius. It undoes the parent's
// padding (and the gap above the layout) with negative margins, then pads
// its own contents back in by the same amounts, so the text lines up with
// the widgets below while the white reaches the edges.
const HeaderBand = styled.div`
  background: ${colors2.white};
  margin: calc(-1 * (${({ $bleed }) => $bleed.y} + ${({ $bleed }) => $bleed.top}px))
    calc(-1 * ${({ $bleed }) => $bleed.x}) 0;
  padding: ${({ $bleed }) => $bleed.y} ${({ $bleed }) => $bleed.x} 20px;
  display: flex;
  flex-direction: column;
  gap: 14px;
`;

// Back on the left; the sidebar's show-filters affordance on the right when
// the sidebar is collapsed (the parent's own toolbar row steps aside for
// this band).
const HeaderToolbar = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  /* Tighter to the title than the band's regular row gap. */
  margin-bottom: -8px;
`;

// The bar and the lines under it share one width, so the lines never run
// wider than the bar they explain.
const BarColumn = styled.div`
  width: 100%;
  max-width: 560px;
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

// One signal category: a section with no card chrome — its heading, then
// its finding cards as a grid.
const CategorySection = styled.section`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

// Same anatomy as the Lenses widget headers (LensesTab/TeamLensBlock):
// a 0.6rem flex row with a small mark, and UI_BODY_SEMIBOLD_SM_DF for the
// text — NOT UI_BODY_BOLD, which reads a size too heavy next to them.
const CategoryHeading = styled.div`
  display: flex;
  align-items: center;
  gap: 0.6rem;
`;

// Category sections, stacked.
const WidgetStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 28px;
  margin-top: 24px;
`;

// A finding card is never narrower than this: the grid fits as many as the
// row can hold at this width and wraps the rest (one per row in a narrow
// host such as the topic side panel).
const FINDING_CARD_MIN = "26rem"; // 260px

// The finding cards as a grid that fills from the left at the card minimum
// and leaves the rest of the row empty (auto-fill), rather than stretching a
// short row's cards across — the shared WrapperCardGrid's own behaviour,
// at this drilldown's card minimum (its `minWidth`) with tighter gaps.
const CategoryCards = ({ children }) => (
  <WrapperCardGrid minWidth={FINDING_CARD_MIN} style={{ gridColumnGap: 12, gridRowGap: 12 }}>
    {children}
  </WrapperCardGrid>
);

// `$muted` is the unplaced card's look: the same anatomy as a finding, but
// on a grey ground, because a finding is a CLAIM and this card is just
// evidence that hasn't formed one. No border at rest; hover reveals one, the
// same interaction as the Annotation Explorer's cards.
const FindingCard = styled.div`
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
  align-items: flex-start;
  padding: 14px;
  border: 1px solid transparent;
  border-radius: 10px;
  cursor: pointer;
  background: ${({ $selected, $muted }) =>
    $selected ? colors2.grey5 : $muted ? colors2.grey2 : colors2.white};
  transition: border-color 0.15s ease;

  &:hover {
    border-color: ${colors2.grey20};
  }
`;

// Pinned to the bottom of the card so the line and the counts line up
// across cards of differing text length: the sparkline (when there is one)
// sits directly above the footer, both pushed down together.
// Same mark as the SentimentBar's legend dot, so the widget title and the
// bar's legend read as the same key.
const CategoryDot = styled.span`
  width: 8px;
  height: 12px;
  border-radius: 999px;
  background: ${({ $color }) => $color};
  flex-shrink: 0;
`;

// The grid's unplaced card: a cluster card on the muted ground.
const UnplacedClusterCard = styled(ClickableCard)`
  background: ${colors2.grey2};
`;

// `leadingFirst` reorders by share, biggest first (stable sort, so ties keep
// the canonical order). The cluster cards use it: the card's story is "what
// leads here", so that segment opens the bar and carries the only label.
const categorySegments = (signalCategoryCounts = {}, categoryTheme, { leadingFirst = false } = {}) => {
  const total = categoryTheme.list.reduce(
    (s, c) => s + (signalCategoryCounts[c.value] || 0),
    0
  );
  if (!total) return [];
  const segments = categoryTheme.list.map(({ value: category, display }) => ({
    key: category,
    category,
    title: display,
    value: Math.round(((signalCategoryCounts[category] || 0) / total) * 100),
    // Raw count, rendered in the legend between percent and label.
    count: signalCategoryCounts[category] || 0,
    color: categoryTheme.fillOf(category),
  }));
  return leadingFirst ? segments.sort((a, b) => b.count - a.count) : segments;
};

// 3-level buckets drill into their encapsulations; legacy flat docs treat
// the bucket itself as its single row.
const rowsForBucket = (bucket) => (bucket.children?.length ? bucket.children : [bucket]);

// A set of parked observations as a ROW the side panel can open — the same
// shape a finding row has (label, description, observations, datasets,
// annotationCount) minus a signalId, since it's many signals' worth of
// evidence rather than one claim. `isUnplaced` lets the panel header say so.
const unplacedRowFor = ({ nodeId, category, observations }) => ({
  nodeId: `${nodeId}:unplaced:${category || "none"}`,
  isUnplaced: true,
  label: "Unplaced observations",
  // Same wording as the grid's unplaced card, one level down: findings are
  // the subtopics here.
  description: "Miscellaneous observations not fitting any sub-topic. Possible outliers.",
  observations,
  datasets: [...new Set(observations.map((o) => o.dataset).filter(Boolean))],
  annotationCount: observations.length,
});
// The same row from a COUNT (Topics V2 has no observations, only the parked
// list's per-category counts). `parkedOf` + `category` let the panel scope
// its annotation list to this topic's parked pseudo sub-topic and category.
const unplacedRowFromCount = ({ nodeId, category, count, datasets }) => ({
  nodeId: `${nodeId}:unplaced:${category || "none"}`,
  isUnplaced: true,
  parkedOf: String(nodeId),
  category: category || null,
  label: "Unplaced observations",
  description: "Miscellaneous observations not fitting any sub-topic. Possible outliers.",
  observations: [],
  datasets: datasets || [],
  annotationCount: count,
});

// The muted finding-shaped card for an unplaced row. No signal badge (the
// widget it sits in already names the category); label and description sit
// in grey so the card reads as quieter than the findings beside it.
const UnplacedFindingCard = ({ row, selected, onClick, titleByContentType }) => (
  <FindingCard $muted $selected={selected} onClick={onClick}>
    <div>
      <UI_BODY_SEMIBOLD_SM style={{ paddingBottom: 4, color: colors2.grey60 }}>{row.label}</UI_BODY_SEMIBOLD_SM>
      <UI_BODY_SM style={{ color: colors2.grey60 }}>{row.description}</UI_BODY_SM>
    </div>
    <CardTail>
      <CardFooter>
        <ObservationCount count={row.annotationCount} />
        <DatasetCircles datasets={row.datasets} titleByContentType={titleByContentType} />
      </CardFooter>
    </CardTail>
  </FindingCard>
);

// `seriesScope` (Topics V2): { activeFilters, topicClusteringLens, period,
// dateFieldMap } — the scope the drilldown's lines are counted over. With
// it, the drilldown requests its own two time series (per signal for the
// header's category lines, per sub-topic for the cards' sparklines),
// answered from Elasticsearch, only while it is on screen. Without it (V1),
// the lines are drawn from the observation arrays the rows carry. While
// loading, a faint skeleton holds each line's place.
const LineSkeleton = ({ height }) => <SkeletonLoader style={{ height, width: "100%", opacity: 0.45, borderRadius: 4 }} />;
const BucketDrilldown = ({ bucket, signalMetaById, titleByContentType, onBack = null, onRowClick, openRowNodeId, period, prevWindow, prevSignalCategoryCounts, prevLoading = false, headerBleed, onShowFilters = null, seriesScope = null }) => {
  const categoryTheme = useSignalCategories();
  const series = useDrilldownSeries({ bucket, signalMetaById, seriesScope });
  // Group the bucket's encapsulations by their majority-signal's category,
  // then sort each card's rows by signal (display name, then statement) so
  // same-signal rows sit together instead of dispersing through the card.
  const rowsByCategory = new Map();
  rowsForBucket(bucket).forEach((row) => {
    const category = row.signalId ? signalMetaById[row.signalId]?.category : null;
    if (!category) return;
    if (!rowsByCategory.has(category)) rowsByCategory.set(category, []);
    rowsByCategory.get(category).push(row);
  });
  for (const rows of rowsByCategory.values()) {
    rows.sort((a, b) => {
      const byObservations = (b.annotationCount || 0) - (a.annotationCount || 0);
      if (byObservations) return byObservations;
      const sigA = signalMetaById[a.signalId]?.display || "";
      const sigB = signalMetaById[b.signalId]?.display || "";
      return sigA.localeCompare(sigB) || (a.label || "").localeCompare(b.label || "");
    });
  }

  // Parked observations carry a signal too, so they belong INSIDE their
  // category's widget rather than in a strip of their own. Anything whose
  // signal has no category (or no signal at all) can't be placed that way and
  // falls through to the tail section below the widgets.
  const parkedByCategory = new Map();
  const uncategorisedParked = [];
  (bucket.parkedObservations || []).forEach((o) => {
    const category = o.signalId ? signalMetaById[o.signalId]?.category : null;
    if (!category) {
      uncategorisedParked.push(o);
      return;
    }
    if (!parkedByCategory.has(category)) parkedByCategory.set(category, []);
    parkedByCategory.get(category).push(o);
  });
  for (const list of parkedByCategory.values()) {
    list.sort((a, b) => (a.title || "").localeCompare(b.title || ""));
  }
  // Topics V2: no parked observations, but the parked list's counts per
  // category — one unplaced card per category from those, and the remainder
  // (parked under a signal with no category) as the tail card.
  const parkedCounts = bucket.parked?.signalCategoryCounts;
  const parkedFromCounts = !!parkedCounts && !(bucket.parkedObservations || []).length;
  const parkedCountByCategory = new Map();
  let uncategorisedParkedCount = 0;
  if (parkedFromCounts) {
    let categorised = 0;
    for (const [category, n] of Object.entries(parkedCounts)) {
      if (n > 0) parkedCountByCategory.set(category, n);
      categorised += n;
    }
    uncategorisedParkedCount = Math.max(0, (bucket.parked?.annotationCount || 0) - categorised);
  }

  // Same leading-first emphasis as the grid cards, carried through to the
  // lines: the bar, its one label, its trend chip and the highlighted line
  // all speak about the same category.
  const segments = categorySegments(bucket.signalCategoryCounts, categoryTheme, { leadingFirst: true });
  const leading = segments[0];

  return (
    <div>
      {/* The clicked card's contents as the page header. */}
      <HeaderBand $bleed={headerBleed}>
        {/* No toolbar at all when the host has neither control to offer
            (a sidebar: its own close button stands in for Back). */}
        {(onBack || onShowFilters) && (
          <HeaderToolbar>
            {/* Same compact ghost as the filter sidebar's Collapse button. */}
            {onBack ? <ButtonGhostCompact S icon="CaretLeft" value="Back" onClick={onBack} /> : <span />}
            {onShowFilters && (
              <ButtonGhostCompact S icon="ArrowLineRight" value="Show Views & Filters" onClick={onShowFilters} />
            )}
          </HeaderToolbar>
        )}
        <div>
          <UI_BODY_SEMIBOLD>{bucket.label}</UI_BODY_SEMIBOLD>
          {bucket.description && (
            <UI_BODY_SM style={{ color: colors2.grey60 }}>{bucket.description}</UI_BODY_SM>
          )}
        </div>
        <BarColumn>
          <SentimentBar
            segments={segments}
            emphasisKey={leading?.key}
            emphasisLegendSuffix={
              <LeadingCategoryTrend
                category={leading?.category}
                signalCategoryCounts={bucket.signalCategoryCounts}
                prevSignalCategoryCounts={prevSignalCategoryCounts}
                prevWindow={prevWindow}
                loading={prevLoading}
              />
            }
            showLegend
            roundedEnds
          />
          {series?.loading ? (
            <LineSkeleton height={80} />
          ) : (
            <CategorySparklines
              bucket={bucket}
              signalMetaById={signalMetaById}
              period={period}
              emphasisCategory={leading?.category}
              countsByCategory={series?.byCategory || null}
            />
          )}
        </BarColumn>
        <ObservationCount count={bucket.annotationCount} />
      </HeaderBand>

      {/* One widget per signal category, stacked. Findings as a card grid;
          the category's loose observations ride as the last card in the same
          grid, so a category reads as one unit. */}
      <WidgetStack>
        {/* Sections follow the bar: biggest share first. `segments` is
            already in that order (leading-first); it's empty only when the
            bucket has no categorised observations, when the canonical order
            is as good as any. */}
        {(segments.length
          ? segments.map(({ category, title }) => ({ category, title }))
          : categoryTheme.list.map((c) => ({ category: c.value, title: c.display }))
        )
          .filter(({ category }) => rowsByCategory.has(category) || parkedByCategory.has(category) || parkedCountByCategory.has(category))
          .map(({ category, title }) => {
          const color = categoryTheme.fillOf(category);
          const rows = rowsByCategory.get(category) || [];
          const parked = parkedByCategory.get(category) || [];
          const parkedCount = parkedCountByCategory.get(category) || 0;
          return (
            <CategorySection key={category}>
              <CategoryHeading>
                <CategoryDot $color={color} />
                <UI_BODY_SEMIBOLD_SM_DF style={{ color: colors2.grey80 }}>
                  {title} ({rows.length})
                </UI_BODY_SEMIBOLD_SM_DF>
              </CategoryHeading>
              {/* Findings, then — last in the same grid — the category's
                  unplaced observations as one muted card. */}
              <CategoryCards>
                {rows.map((row) => {
                  const signalMeta = row.signalId ? signalMetaById[row.signalId] : null;
                  return (
                    <FindingCard
                      key={String(row.nodeId)}
                      $selected={String(row.nodeId) === openRowNodeId}
                      onClick={() => onRowClick(row)}
                    >
                      {signalMeta && (
                        <SignalBadge
                          category={signalMeta.category}
                          label={signalMeta.display}
                          size="S"
                          colors={{ bg: categoryTheme.fillOf(signalMeta.category), color: categoryTheme.styleOf(signalMeta.category).textOnFill }}
                        />
                      )}
                      <div>
                        {row.label && (
                          <UI_BODY_SEMIBOLD_SM style={{ paddingBottom: 4 }}>{row.label}</UI_BODY_SEMIBOLD_SM>
                        )}
                        {row.description && (
                          <UI_BODY_SM style={{ color: colors2.grey60 }}>{row.description}</UI_BODY_SM>
                        )}
                      </div>
                      <CardTail>
                        {/* The card's own trend — same minimal line as the
                            header's, over just this finding's observations,
                            coloured by its signal's category and kept faded
                            so it sits under the text. Not clickable here. */}
                        {signalMeta && series?.loading && <LineSkeleton height={48} />}
                        {signalMeta && !series?.loading && (
                          <ObservationSparkline
                            observations={row.observations}
                            countsByBin={series?.bySubtopic?.[String(row.nodeId)]}
                            period={period}
                            color={categoryTheme.accentOf(signalMeta.category)}
                            label={signalMeta.display}
                            opacity={0.2}
                          />
                        )}
                        <CardFooter>
                          <ObservationCount count={row.annotationCount} />
                          <DatasetCircles datasets={row.datasets} titleByContentType={titleByContentType} />
                        </CardFooter>
                      </CardTail>
                    </FindingCard>
                  );
                })}
                {(parked.length > 0 || parkedCount > 0) &&
                  (() => {
                    const row = parked.length
                      ? unplacedRowFor({ nodeId: bucket.nodeId, category, observations: parked })
                      : unplacedRowFromCount({ nodeId: bucket.nodeId, category, count: parkedCount, datasets: bucket.datasets });
                    return (
                      <UnplacedFindingCard
                        key={row.nodeId}
                        row={row}
                        selected={row.nodeId === openRowNodeId}
                        onClick={() => onRowClick(row)}
                        titleByContentType={titleByContentType}
                      />
                    );
                  })()}
              </CategoryCards>
            </CategorySection>
          );
        })}
      </WidgetStack>

      {/* Remainder: parked observations whose signal carries no category, so
          none of the widgets above can hold them — one muted card, no
          badge, opening the same panel. */}
      {(uncategorisedParked.length > 0 || uncategorisedParkedCount > 0) &&
        (() => {
          const row = uncategorisedParked.length
            ? unplacedRowFor({ nodeId: bucket.nodeId, category: null, observations: uncategorisedParked })
            : unplacedRowFromCount({ nodeId: bucket.nodeId, category: null, count: uncategorisedParkedCount, datasets: bucket.datasets });
          return (
            <div style={{ display: "flex", marginTop: 24 }}>
              <UnplacedFindingCard
                row={row}
                selected={row.nodeId === openRowNodeId}
                onClick={() => onRowClick(row)}
                titleByContentType={titleByContentType}
              />
            </div>
          );
        })()}
    </div>
  );
};

// The drilldown's two lines, requested by the drilldown itself (see the
// `seriesScope` note above). Both are counted on the annotations collection,
// bucketed on each dataset's "date that matters" (the same map the period
// filter uses), over the period's own interval, within the scope plus a
// Topics filter for this topic. The signal series folds into category lines
// for the header; the sub-topic series feeds each card's sparkline.
const useDrilldownSeries = ({ bucket, signalMetaById, seriesScope }) => {
  const { activeFilters = [], topicClusteringLens, period, dateFieldMap } = seriesScope || {};
  const enabled = !!seriesScope && !!bucket && !!period?.interval && !!dateFieldMap;
  const drillFilters = useMemo(
    () => (enabled ? [...activeFilters, topicNodeFilter(String(bucket.nodeId), topicClusteringLens)] : []),
    [enabled, activeFilters, bucket, topicClusteringLens]
  );
  const common = {
    contentType: "annotations",
    dateValuePathByContentType: dateFieldMap,
    timelineRange: period ? { from: period.from, to: period.to } : undefined,
    interval: period?.interval,
    activeFilters: drillFilters,
    queryOptions: { enabled },
  };
  const { data: signalRows, isLoading: signalLoading } = useAggrMetricsAcrossTimeAndDimension({
    ...common,
    vizCardId: `topicsV2:signals:${bucket?.nodeId}`,
    dimensionValuePath: "annotations.tags.tags",
  });
  const { data: subtopicRows, isLoading: subtopicLoading } = useAggrMetricsAcrossTimeAndDimension({
    ...common,
    vizCardId: `topicsV2:subtopics:${bucket?.nodeId}`,
    dimensionValuePath: enabled ? { dimensionType: "topicClusteringNode", level: "subtopic", parentTopicNodeId: String(bucket.nodeId) } : null,
  });
  return useMemo(() => {
    if (!seriesScope) return null;
    const byCategory = {};
    for (const row of signalRows || []) {
      for (const d of row.dimensions || []) {
        const category = signalMetaById[String(d.value)]?.category;
        if (!category || !d.count) continue;
        (byCategory[category] ||= {})[row.timeInterval] = (byCategory[category][row.timeInterval] || 0) + d.count;
      }
    }
    const bySubtopic = {};
    for (const row of subtopicRows || []) {
      for (const d of row.dimensions || []) {
        (bySubtopic[String(d.value)] ||= {})[row.timeInterval] = d.count;
      }
    }
    return { byCategory, bySubtopic, loading: signalLoading || subtopicLoading };
  }, [seriesScope, signalRows, subtopicRows, signalLoading, subtopicLoading, signalMetaById]);
};

// selectedBucket state lives in the parent so layout-independent chrome can
// hide while drilled in. `unplacedRow` is the tree-level leftovers no bucket
// took, already shaped as a row so the grid's card opens it like a finding.
// `onAskAI(bucket)`: opens the Ask AI chat anchored to this topic (split
// tenants). Optional — layouts without a chat entry pass nothing.
//
// The "vs last period" chip on every card and on the drilldown header reads
// the previous window's per-category counts. The host says which window:
//   prevScope   { topicClusteringLens, activeFilters } — the previous
//               window's scope; the layout requests the counts itself, sent
//               only once the cards have painted.
// `drilldownScope` is the drilldown's line scope (see BucketDrilldown).
// `drilldownBackButton`: false for a host whose own close button stands in
// for Back (the drilldown in a side panel). `gridMinWidth`: the topic
// cards' minimum width (a narrow host passes a smaller one).
export const CardGridLayout = ({ tree, signalMetaById, titleByContentType, selectedBucket, onSelectBucket, onRowClick, openRowNodeId, period, prevWindow, prevScope = null, headerBleed = { x: "0px", y: "0px", top: 0 }, onShowFilters = null, unplacedRow = null, drilldownScope = null, onAskAI = null, drilldownBackButton = true, gridMinWidth = "46rem" }) => {
  const categoryTheme = useSignalCategories();
  const { data: prevCounts } = useGetTopicClusteringTreeCounts({
    topicClusteringLens: prevScope?.topicClusteringLens,
    activeFilters: prevScope?.activeFilters || [],
    enabled: !!prevScope && !!prevWindow && !!tree,
  });
  const prevCountsByNodeId = useMemo(() => {
    if (!prevScope || !prevWindow || !prevCounts) return null;
    return Object.fromEntries((prevCounts.tree || []).map((topic) => [String(topic.nodeId), topic.signalCategoryCounts || {}]));
  }, [prevScope, prevWindow, prevCounts]);
  const prevPending = !!prevScope && !!prevWindow && !prevCountsByNodeId;
  if (selectedBucket) {
    return (
      <BucketDrilldown
        bucket={selectedBucket}
        signalMetaById={signalMetaById}
        titleByContentType={titleByContentType}
        onBack={drilldownBackButton ? () => onSelectBucket(null) : null}
        onRowClick={onRowClick}
        openRowNodeId={openRowNodeId}
        period={period}
        prevWindow={prevWindow}
        prevSignalCategoryCounts={prevCountsByNodeId?.[String(selectedBucket.nodeId)]}
        prevLoading={prevPending}
        headerBleed={headerBleed}
        onShowFilters={onShowFilters}
        seriesScope={drilldownScope}
      />
    );
  }

  // Buckets with the most observations first.
  const buckets = [...(tree.tree || [])].sort(
    (a, b) => (b.annotationCount || 0) - (a.annotationCount || 0)
  );

  return (
    <WrapperCardGrid minWidth={gridMinWidth}>
      {buckets.map((bucket) => {
        // Leading category first; only it gets a label, the rest dim and
        // fold into a hover tooltip. Its trend chip rides at the end of that
        // one legend line, so label and trend read as a single statement.
        const segments = categorySegments(bucket.signalCategoryCounts, categoryTheme, { leadingFirst: true });
        const leading = segments[0];
        return (
          <TopicCard
            key={String(bucket.nodeId)}
            title={bucket.label}
            description={bucket.description}
            segments={segments}
            emphasisKey={leading?.key}
            emphasisLegendSuffix={
              <LeadingCategoryTrend
                category={leading?.category}
                signalCategoryCounts={bucket.signalCategoryCounts}
                prevSignalCategoryCounts={prevCountsByNodeId?.[String(bucket.nodeId)]}
                prevWindow={prevWindow}
                loading={prevPending}
              />
            }
            count={bucket.annotationCount}
            datasets={bucket.datasets}
            titleByContentType={titleByContentType}
            // The Ask AI entry sits beside the dataset circles and must not
            // also open the drilldown.
            footerRight={
              onAskAI && (
                <ButtonGhostCompact
                  S
                  icon="Sparkle"
                  value="Ask AI"
                  // oolib debounces onClick by 200ms, so stopping
                  // propagation inside the handler is too late — the card's
                  // drilldown would open first. This prop is applied
                  // synchronously in the button's own wrapper.
                  stopPropagation
                  onClick={() => onAskAI(bucket)}
                />
              )
            }
            onClick={() => onSelectBucket(bucket)}
          />
        );
      })}
      {/* Last in the grid: the observations no topic would take, as one
          muted cluster card that opens them in the finding panel. */}
      {unplacedRow && (
        <UnplacedClusterCard onClick={() => onRowClick(unplacedRow)}>
          <div>
            <UI_BODY_SEMIBOLD style={{ color: colors2.grey60 }}>{unplacedRow.label}</UI_BODY_SEMIBOLD>
            <UI_BODY_SM style={{ color: colors2.grey60 }}>{unplacedRow.description}</UI_BODY_SM>
          </div>
          <CardTail>
            <CardFooter>
              <ObservationCount count={unplacedRow.annotationCount} />
              <DatasetCircles datasets={unplacedRow.datasets} titleByContentType={titleByContentType} />
            </CardFooter>
          </CardTail>
        </UnplacedClusterCard>
      )}
    </WrapperCardGrid>
  );
};
